/**
 * Types for the QR Label Generator (PBI #18337).
 *
 * The generator produces one print-ready label per item, from three input
 * sources: an order's roster (same shape the barcode modal uses), a manual
 * entry form, and a CSV/batch import. All three normalise into `QrLabelItem`.
 */

/** Symbologies supported by the bwip-js engine. */
export type Symbology = 'qr' | 'microqr' | 'datamatrix' | 'code128' | 'code39';

/** What the generated symbol actually encodes. */
export type EncodeMode = 'short' | 'fields' | 'resolver';

/** QR / Micro QR error-correction level. `L` yields the smallest symbol. */
export type Ecc = 'L' | 'M' | 'Q' | 'H';

/** Where a batch of items came from — drives which inputs are editable. */
export type LabelSource = 'order' | 'manual' | 'csv';

/** The two-dimensional symbologies (mm-per-module readability applies). */
export const TWO_D_SYMBOLOGIES: readonly Symbology[] = ['qr', 'microqr', 'datamatrix'];

/** The one-dimensional (linear) symbologies — carry the code only. */
export const ONE_D_SYMBOLOGIES: readonly Symbology[] = ['code128', 'code39'];

/**
 * A single label. Fields above the divider are the Template01 export columns
 * (in the exact order Waleed specified); the rest are generator inputs that
 * do NOT feed the Excel export (SKU code, web address) plus provenance.
 */
export interface QrLabelItem {
  // --- Template01 columns (exact order) ---
  product_name?: string;
  design_name?: string;
  /** "Name" — the player/roster name. */
  player_name?: string;
  name_font_name?: string;
  /** "Number" — the player/roster number. */
  player_number?: string;
  number_font_name?: string;
  size?: string;
  quantity?: number;
  logo1?: string;

  // --- Generator inputs (not Template01 columns) ---
  order_no?: string;
  /** The full SKU name printed on the label. */
  sku_name?: string;
  /** The short code embedded in the symbol (auto-generated if absent). */
  code: string;
  /** Optional separate SKU short code. */
  sku_code?: string;

  /** Page/sheet reference carried over from the roster, when present. */
  page_reference?: number;
}

/** User-configurable generation settings (the modal's settings bar). */
export interface QrSettings {
  symbology: Symbology;
  encodeMode: EncodeMode;
  ecc: Ecc;
  /** Intended printed size of the symbol on the label, in millimetres. */
  printSizeMm: number;
  /** Symbol (foreground) colour, hex. */
  dotColor: string;
  /** Background colour, hex. */
  bgColor: string;
  labelWidthMm: number;
  labelHeightMm: number;
  /** Print the human-readable meta text on the label. */
  showLabelText: boolean;
  /** Include the order no in the "Order / SKU / Size" payload. */
  includeOrder: boolean;
  /** Include the SKU / product name in the "Order / SKU / Size" payload. */
  includeSku: boolean;
  /** Include the size in the "Order / SKU / Size" payload. */
  includeSize: boolean;
  /** Short domain used for resolver payloads (`<domain>/?<code>`). */
  shortDomain: string;
}

/** Readability verdict level for the fabric meter. */
export type MeterLevel = 'ok' | 'warn' | 'bad' | 'info';

/** Result of the fabric readability calculation. */
export interface ReadabilityVerdict {
  level: MeterLevel;
  /** Short headline, e.g. "✅ Good for fabric". */
  text: string;
  /** mm per module (0 for 1D symbologies where it does not apply). */
  mmPerModule: number;
  /** Module count along one edge (0 for 1D). */
  moduleCount: number;
  /** Minimum print size (mm) needed to reach 0.5 mm/module. */
  minMmForHalf: number;
  /** Longer explanatory line. */
  detail: string;
}

/** Default settings — Standard QR, 50.8 × 38.1 mm sheet preset (rev-15). */
export const DEFAULT_QR_SETTINGS: QrSettings = {
  symbology: 'qr',
  encodeMode: 'short',
  ecc: 'L',
  printSizeMm: 25,
  dotColor: '#000000',
  bgColor: '#ffffff',
  labelWidthMm: 50.8,
  labelHeightMm: 38.1,
  showLabelText: true,
  includeOrder: true,
  includeSku: true,
  includeSize: true,
  shortDomain: 'cstm.co',
};

/** Human labels for the symbology dropdown. */
export const SYMBOLOGY_LABELS: Record<Symbology, string> = {
  qr: 'Standard QR (default)',
  microqr: 'Micro QR',
  datamatrix: 'DataMatrix',
  code128: 'Code 128',
  code39: 'Code 39',
};

/** Human labels for the encode-mode dropdown. */
export const ENCODE_MODE_LABELS: Record<EncodeMode, string> = {
  short: 'Short code only',
  fields: 'Order / SKU / Size',
  resolver: 'Resolver (tiny)',
};
